import type { Query, Feedback } from "./types";

/**
 * Data type expected from the data source
 */
export type ChartData = {
  value: number | null;
  date: Date;
  unit?: string;
  source?: string;
}[];


export type Statistics = {
  model: string;
  name: { [key: string]: string };
  description: { [key: string]: string };
  keywords: { [key: string]: string[] };
  is_daily: boolean;
  disabled: boolean;
}[];

export type Fields = {
  name: string;
  values: {
    name: string;
    description?: string;
    keywords?: string[];
    disabled?: boolean;
  }[];
}[];

export type Field = {
  name: { [key: string]: string };
  field: string;
  options: {
    name: { [key: string]: string };
    disabled?: boolean;
    parent_country?: { [key: string]: string };
  }[];

  defaults?: {
    name: string;
    disabled?: boolean;
  }[];

  parent_country?: {
    name: string;
    disabled?: boolean;
  }[];
}[];

export type TableData = {
  value: number | string | null;
  column: string;
  dateFrom: Date; // If data is aggregated, dateTo is the first date of the aggregation
  dateTo: Date; // If data is aggregated, dateTo is the last date of the aggregation
  row: string;
  statName: string;
  verbose_options: {[key: string]: string};
  id: number | undefined;
  source: string;
}[];

export type VisualizationData = {
  id: string;
  type: string;
  period: string[];
  metadata: {
    statName: string;
    options: string[];
  }[];
  chartType?: string;
  dataValueType?: string;
  visualizationSize?: string;
};

export let monthShortToLongDict: { [key: string]: string } = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
}

export const monthShortList = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Fetch data from a data source
 *
 * @param query - Query object
 * @returns QueryData object as a promise
 */
export interface DataSourceInterface {
  getChartData: (query: Query) => Promise<ChartData>;
  getTableData: (query: Query) => Promise<TableData>;

  /**
   * Function for getting available statistics from the data source.
   * 
   * Statistics are limited to the ones that are available for user.
   * 
   * @returns Promise of an array of statistics
   */
  getStatistics: () => Promise<Statistics>;

  /**
   * Function for getting available fields for a given statistic.
   * 
   * @param statName - Name of the statistic
   * @returns Promise of an array of fields
   */
  getFields: (statName: string) => Promise<Field>;

  /**
   * Function for updating disabled (not selectable) fields based on selected options.
   * 
   * @param selectedOptions - Selected options
   * @param statName - Name of the statistic
   * @returns Promise of an array of fields
   */
  updateDisabledFields: (selectedOptions: any, statName: string) => Promise<Field>;

  /**
   * Send user's feedback to the data source.
   * 
   * @param feedback - Feedback object
   */
  sendFeedback: (feedback: Feedback) => Promise<any>;

  /**
   * Fetch dictionary entries, optionally filtered by stat model / type context.
   *
   * @param stat_model - Optional stat model (DatawellModels.name)
   * @param type_field - Optional FK field name on the stat model
   * @param type_value - Optional canonical lowercase English type value
   * @returns Promise of dictionary entries array
   */
  getDictionary: (
    stat_model?: string,
    type_field?: string,
    type_value?: string
  ) => Promise<any[]>;
}
